<template>
    <v-container fluid>
        <v-row
            no-gutters
            align="center"
            class="pt-3 pt-sm-4 pb-3 pb-sm-4 px-2 px-sm-4"
            :style="{ 'background-color': '#eeeeee' }"
        >
            <v-col cols="12" sm="auto" class="py-0 d-flex align-center">
                <h1
                    class="text-h6 text-sm-h5 mb-0"
                    :class="$vuetify.breakpoint.xsOnly ? 'mr-2' : 'mr-4'"
                >
                    Role Assignments
                </h1>
            </v-col>
            <v-spacer class="d-none d-sm-flex" />
            <v-col cols="12" sm="auto" class="py-0 pt-2 pt-sm-0">
                <v-btn
                    color="primary"
                    rounded
                    :loading="saving"
                    :disabled="!valid || !hasChanges"
                    @click="save"
                    block
                    class="d-sm-inline-block"
                >
                    {{ $vuetify.breakpoint.smAndDown ? '' : 'Save' }}
                    <v-icon
                        v-if="$vuetify.breakpoint.smAndDown"
                        :class="$vuetify.breakpoint.smAndDown ? '' : 'ml-1'"
                    >
                        mdi-content-save
                    </v-icon>
                </v-btn>
            </v-col>
        </v-row>
        <v-card :loading="loading" flat class="mt-4">
            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <v-card outlined class="mb-4">
                        <v-card-title class="py-2">
                            Document Review
                        </v-card-title>
                        <v-card-text class="pt-2">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-autocomplete
                                        v-model="documentReviewUserId"
                                        :items="users"
                                        item-text="name"
                                        item-value="id"
                                        label="Document Reviewer"
                                        clearable
                                        :loading="loading"
                                        no-data-text="No hay usuarios"
                                    />
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                    <v-card outlined class="mb-4">
                        <v-card-title class="py-2">
                            Calculator Models
                        </v-card-title>
                    </v-card>
                    <v-expansion-panels class="mb-4" accordion multiple>
                        <v-expansion-panel
                            v-for="modelKey in MODEL_KEYS"
                            :key="modelKey"
                        >
                            <v-expansion-panel-header>
                                {{ modelKey }}
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row>
                                    <v-col cols="12" md="4">
                                        <v-autocomplete
                                            v-model="
                                                assignments[modelKey]
                                                    .preconManager
                                            "
                                            :items="users"
                                            item-text="name"
                                            item-value="id"
                                            label="Precon Manager"
                                            clearable
                                            :loading="loading"
                                            no-data-text="No hay usuarios"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-autocomplete
                                            v-model="
                                                assignments[modelKey]
                                                    .accountManager
                                            "
                                            :items="users"
                                            item-text="name"
                                            item-value="id"
                                            label="Account Manager"
                                            clearable
                                            :loading="loading"
                                            no-data-text="No hay usuarios"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-autocomplete
                                            v-model="
                                                assignments[modelKey].estimator
                                            "
                                            :items="users"
                                            item-text="name"
                                            item-value="id"
                                            label="Estimator"
                                            clearable
                                            :loading="loading"
                                            no-data-text="No hay usuarios"
                                        />
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-form>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'

const MODEL_KEYS = [
    '5LS',
    '3LS',
    '1.4LS',
    '1.8LS',
    'HPL',
    'ACS Aluminum',
    'Brake Metals',
]

function getDefaultAssignments() {
    return MODEL_KEYS.reduce((acc, key) => {
        acc[key] = {
            preconManager: null,
            accountManager: null,
            estimator: null,
        }
        return acc
    }, {})
}

function deepEqualAssignments(a, b) {
    for (const key of MODEL_KEYS) {
        const ma = a[key] || {}
        const mb = b[key] || {}
        if (
            (ma.preconManager || null) !== (mb.preconManager || null) ||
            (ma.accountManager || null) !== (mb.accountManager || null) ||
            (ma.estimator || null) !== (mb.estimator || null)
        ) {
            return false
        }
    }
    return true
}

function modelAssignmentsEqual(a, b) {
    const ma = a || {}
    const mb = b || {}
    return (
        (ma.preconManager || null) === (mb.preconManager || null) &&
        (ma.accountManager || null) === (mb.accountManager || null) &&
        (ma.estimator || null) === (mb.estimator || null)
    )
}

function getDirtyRoleAssignments(assignments, originalAssignments) {
    const dirty = {}
    for (const key of MODEL_KEYS) {
        if (
            !modelAssignmentsEqual(assignments[key], originalAssignments[key])
        ) {
            dirty[key] = { ...assignments[key] }
        }
    }
    return dirty
}

export default {
    name: 'ClientRoleAssignments',
    props: {
        setting: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            MODEL_KEYS,
            loading: false,
            saving: false,
            valid: true,
            users: [],
            assignments: getDefaultAssignments(),
            originalAssignments: null,
            documentReviewUserId: null,
            originalDocumentReviewUserId: null,
        }
    },
    computed: {
        hasChanges() {
            if (!this.originalAssignments) return false
            const rolesChanged = !deepEqualAssignments(
                this.assignments,
                this.originalAssignments
            )
            const reviewerChanged =
                (this.documentReviewUserId || null) !==
                (this.originalDocumentReviewUserId || null)
            return rolesChanged || reviewerChanged
        },
    },
    async mounted() {
        await this.loadData()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async loadData() {
            if (!this.setting?.id) return
            try {
                this.loading = true
                const [
                    roleAssignmentsResponse,
                    usersResponse,
                ] = await Promise.all([
                    API.getClientSettingRoleAssignments(this.setting.id),
                    API.getLiteUsers(),
                ])
                this.users = usersResponse?.data?.users || []
                const roleAssignmentsByModel =
                    roleAssignmentsResponse?.roleAssignmentsByModel || {}
                this.documentReviewUserId =
                    roleAssignmentsResponse?.documentReviewUserId ?? null
                this.originalDocumentReviewUserId = this.documentReviewUserId
                const defaultAssignments = getDefaultAssignments()
                for (const key of MODEL_KEYS) {
                    const fromApi = roleAssignmentsByModel[key] || {}
                    this.assignments[key] = {
                        preconManager:
                            fromApi.preconManager ??
                            defaultAssignments[key].preconManager,
                        accountManager:
                            fromApi.accountManager ??
                            defaultAssignments[key].accountManager,
                        estimator:
                            fromApi.estimator ??
                            defaultAssignments[key].estimator,
                    }
                }
                this.originalAssignments = JSON.parse(
                    JSON.stringify(this.assignments)
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async save() {
            if (!this.hasChanges || !this.setting?.id) return
            try {
                this.saving = true
                // Send only models that changed so backend merge avoids overwriting concurrent edits
                const roleAssignmentsByModel = getDirtyRoleAssignments(
                    this.assignments,
                    this.originalAssignments
                )
                const roleAssignmentsResponse = await API.updateClientSettingRoleAssignments(
                    this.setting.id,
                    roleAssignmentsByModel,
                    this.documentReviewUserId
                )
                const roleAssignments =
                    roleAssignmentsResponse?.roleAssignmentsByModel || {}
                for (const key of MODEL_KEYS) {
                    this.assignments[key] = { ...roleAssignments[key] }
                }
                this.originalAssignments = JSON.parse(
                    JSON.stringify(this.assignments)
                )
                this.documentReviewUserId =
                    roleAssignmentsResponse?.documentReviewUserId ?? null
                this.originalDocumentReviewUserId = this.documentReviewUserId
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.saving = false
            }
        },
    },
}
</script>
